import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const mount = document.getElementById('clay-viewer-mount');
if (!mount) { /* not on a page with the 3D viewer — exit silently */ }
else {

const sideUrl = mount.dataset.labelSide || 'imgs/featured-label-side.jpg';
const topUrl  = mount.dataset.labelTop  || 'imgs/featured-label-top.jpg';

// ── Renderer ──────────────────────────────────────────────
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
mount.appendChild(renderer.domElement);

// ── Scene + Camera ────────────────────────────────────────
const scene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
camera.position.set(3.8, 3, 3.8);

// ── Lighting ──────────────────────────────────────────────
const key = new THREE.DirectionalLight(0xfff5e0, 2.8);
key.position.set(3, 6, 4);
scene.add(key);

const fill = new THREE.DirectionalLight(0xc8e0ff, 0.7);
fill.position.set(-4, 1, 2);
scene.add(fill);

const rim = new THREE.DirectionalLight(0xffe8c0, 1.2);
rim.position.set(0, 2, -4);
scene.add(rim);

scene.add(new THREE.AmbientLight(0xffffff, 0.3));

// ── Textures ──────────────────────────────────────────────
// LoadingManager fires onLoad once BOTH textures are ready,
// triggering the crossfade: canvas fades in, placeholder fades out.
const manager = new THREE.LoadingManager();
manager.onLoad = () => mount.classList.add('ready');
const loader = new THREE.TextureLoader(manager);
loader.setCrossOrigin('anonymous');

const labelTex = loader.load(sideUrl);
labelTex.colorSpace = THREE.SRGBColorSpace;
labelTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
labelTex.wrapS = THREE.RepeatWrapping;
labelTex.offset.x = 0.5;

const lidTopTex = loader.load(topUrl);
lidTopTex.colorSpace = THREE.SRGBColorSpace;
lidTopTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
lidTopTex.center.set(0.5, 0.5);
lidTopTex.repeat.set(0.94, 0.94);

// ── Build jar ─────────────────────────────────────────────
const R  = 1.00;
const BH = 0.72;
const LH = 0.30;
const LR = 1.00;

const LABEL_MARGIN = 0.07;
const LABEL_H = BH * (1 - 2 * LABEL_MARGIN);
const LABEL_R = R + 0.002;

const BODY_HEX    = 0x0a0a0a;
const LID_RIM_HEX = 0xc87020;

const bodyMat = new THREE.MeshStandardMaterial({
  color: BODY_HEX, roughness: 0.55, metalness: 0.08,
});
const labelMat = new THREE.MeshStandardMaterial({
  map: labelTex, roughness: 0.22, metalness: 0.12,
});
const darkMat = new THREE.MeshStandardMaterial({
  color: BODY_HEX, roughness: 0.5, metalness: 0.1,
});
const lidSideMat = new THREE.MeshStandardMaterial({
  color: LID_RIM_HEX, roughness: 0.45, metalness: 0.25,
});
const lidTopMat = new THREE.MeshStandardMaterial({
  map: lidTopTex, roughness: 0.28, metalness: 0.22,
});

const group = new THREE.Group();

group.add(new THREE.Mesh(new THREE.CylinderGeometry(R, R, BH, 96, 1, true), bodyMat));

const labelMesh = new THREE.Mesh(
  new THREE.CylinderGeometry(LABEL_R, LABEL_R, LABEL_H, 96, 1, true),
  labelMat
);
labelMesh.position.y = 0;
group.add(labelMesh);

const bottom = new THREE.Mesh(new THREE.CircleGeometry(R, 64), darkMat);
bottom.rotation.x = Math.PI / 2;
bottom.position.y = -BH / 2;
group.add(bottom);

const lidY = BH / 2 + LH / 2;

const lidSide = new THREE.Mesh(new THREE.CylinderGeometry(LR, LR, LH, 96, 1, true), lidSideMat);
lidSide.position.y = lidY;
group.add(lidSide);

const lidTop = new THREE.Mesh(new THREE.CircleGeometry(LR, 64), lidTopMat);
lidTop.rotation.x = -Math.PI / 2;
lidTop.position.y = lidY + LH / 2;
group.add(lidTop);

const lidBot = new THREE.Mesh(new THREE.CircleGeometry(LR, 64), darkMat);
lidBot.rotation.x = Math.PI / 2;
lidBot.position.y = BH / 2;
group.add(lidBot);

group.position.y = -(BH / 2 + LH) / 2 + 0.08;
group.scale.setScalar(1.3);
scene.add(group);

// ── Controls ──────────────────────────────────────────────
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping  = true;
controls.dampingFactor  = 0.08;
controls.target.set(0, 0.15, 0);
controls.autoRotate     = true;
controls.autoRotateSpeed = 1.5;
controls.enableZoom     = false;
controls.enablePan      = false;
controls.minPolarAngle  = Math.PI * 0.2;
controls.maxPolarAngle  = Math.PI * 0.72;

const hint = mount.querySelector('.clay-hint');
mount.addEventListener('pointerdown', () => {
  controls.autoRotate = false;
  if (hint) hint.classList.add('hidden');
});
mount.addEventListener('pointerup', () => {
  setTimeout(() => { controls.autoRotate = true; }, 2500);
});

// ── Resize ────────────────────────────────────────────────
const TARGET_HFOV = 30 * Math.PI / 180;
function resize() {
  const w = mount.clientWidth, h = mount.clientHeight;
  const aspect = w / h;
  camera.aspect = aspect;
  camera.fov = 2 * Math.atan(Math.tan(TARGET_HFOV / 2) / aspect) * 180 / Math.PI;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
}
resize();
new ResizeObserver(resize).observe(mount);

// ── Animate ───────────────────────────────────────────────
(function tick() {
  requestAnimationFrame(tick);
  controls.update();
  renderer.render(scene, camera);
})();

} // end if (mount)
